import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchArticles, fetchCategories } from "@/lib/api";
import { slugify } from "@/lib/slugify";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/Reveal";
import { ArticleCard } from "@/components/ArticleExplorer";
import { Newsletter } from "@/components/Newsletter";

export default function CategoryPage() {
    const { slug } = useParams();
    const { data: categories = [], isLoading: loadingCats } = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });

    const category = useMemo(
        () => categories.find((c) => slugify(c.name) === slug),
        [categories, slug]
    );

    const { data: articles = [], isLoading: loadingArticles } = useQuery({
        queryKey: ["category-articles", category?.name],
        queryFn: () => fetchArticles({ category: category.name }),
        enabled: Boolean(category),
    });

    useSEO({
        title: category ? `${category.name} — Investidor Comum` : "Categorias — Investidor Comum",
        description: category ? `Todos os guias de ${category.name} do Investidor Comum, em português claro.` : undefined,
        url: window.location.href,
    });

    const otherCategories = categories.filter((c) => c.name !== category?.name);

    return (
        <main data-testid="category-page" className="pt-28 md:pt-36 pb-20">
            <div className="max-w-7xl mx-auto px-4 md:px-8">
                <Link to="/" data-testid="category-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-gold transition-colors mb-8">
                    <ArrowLeft size={14} /> Home
                </Link>

                {loadingCats ? (
                    <div className="h-14 w-72 bg-white/5 animate-pulse" />
                ) : !category ? (
                    <div data-testid="category-not-found">
                        <p className="font-serif text-4xl italic text-slate-400">Categoria não encontrada.</p>
                        <Link to="/" className="inline-block mt-6 text-gold text-sm uppercase tracking-[0.2em] hover:text-golddark transition-colors">
                            Voltar para a home
                        </Link>
                    </div>
                ) : (
                    <>
                        <span className="block text-xs uppercase tracking-[0.3em] text-gold font-medium">Categoria</span>
                        <h1 data-testid="category-title" className="font-serif text-4xl md:text-6xl tracking-tight mt-3">{category.name}</h1>
                        <p className="mt-4 text-sm md:text-base text-slate-500">
                            {articles.length} {articles.length === 1 ? "guia publicado" : "guias publicados"}
                        </p>

                        <div className="mt-12 md:mt-16">
                            {loadingArticles ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {[...Array(3)].map((_, i) => <div key={i} className="border border-white/5 bg-paper animate-pulse h-72" />)}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {articles.map((a, i) => (
                                        <Reveal key={a.slug} delay={(i % 3) * 0.07}>
                                            <ArticleCard article={a} index={i} />
                                        </Reveal>
                                    ))}
                                </div>
                            )}
                        </div>

                        {otherCategories.length > 0 && (
                            <div className="mt-20 pt-10 border-t border-white/10">
                                <span className="text-xs uppercase tracking-[0.25em] text-slate-500">Outras categorias</span>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {otherCategories.map((c) => (
                                        <Link
                                            key={c.id}
                                            to={`/categoria/${slugify(c.name)}`}
                                            data-testid={`category-link-${slugify(c.name)}`}
                                            className="group flex items-center gap-2 text-xs uppercase tracking-[0.15em] px-4 py-2 border border-white/15 text-slate-400 hover:border-gold/60 hover:text-gold transition-colors"
                                        >
                                            {c.name}
                                            <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="mt-20 md:mt-28">
                <Newsletter />
            </div>
        </main>
    );
}
